import * as R from 'ramda'
import {
  type AppointmentEventType,
  moment,
  type NamedEntity,
  type Nil,
} from '@pbt/pbt-ui-components'

import { AppointmentStateColorMap } from '~/constants/event'
import { SlotType } from '~/constants/schedulerConstants'
import i18n from '~/locales/i18n'
import type {
  BoardingSchedule,
  TimetableEvent,
  TimetableFilter,
  TimetableSlot,
  TimetableSlotPosition,
} from '~/types'
import { getUrlSearchParam } from '~/utils'
import { formatHourRange } from '~/utils/time'

export const getSlotId = (slot: TimetableSlot) =>
  slot.appointment || slot.busyTime

export const dragDropIdToReal = (dragDropId: string) => dragDropId.split('-')[0]
export const realIdToDragDrop = (id: string, type: string) =>
  type ? `${id}-${type}` : id

export const getFilterGroupsBySelectedIds = (
  defaultFilters: TimetableFilter<NamedEntity>[],
  selectedLabels: string[],
) =>
  selectedLabels?.length
    ? defaultFilters.map((item) => ({
        ...item,
        selected: selectedLabels.includes(item.id),
      }))
    : defaultFilters

export const getSlotIdForPlaceholder = (
  slots: TimetableSlot[],
  appointment: TimetableEvent | undefined,
  appointmentsMap: Record<string, TimetableEvent>,
) => {
  if (!appointment) {
    return undefined
  }

  const slot = slots.find((s) => {
    const id = getSlotId(s)
    const slotAppointment = id ? appointmentsMap[id] : undefined
    return moment(slotAppointment?.scheduledStartDatetime).isAfter(
      moment(appointment.scheduledStartDatetime),
    )
  })

  return slot && getSlotId(slot)
}

export const getSlotsAreOverlapping = (
  slotOne: TimetableSlot,
  slotTwo: TimetableSlot,
) => {
  const notOverlapping =
    moment(slotOne.interval.to).isSameOrBefore(slotTwo.interval.from) ||
    moment(slotTwo.interval.to).isSameOrBefore(slotOne.interval.from)

  return !notOverlapping
}

export const getSlotsAreOverlappingAndSameType = (
  slotOne: TimetableSlot,
  slotTwo: TimetableSlot,
) => {
  const notOverlapping =
    moment(slotOne.interval.to).isSameOrBefore(slotTwo.interval.from) ||
    moment(slotTwo.interval.to).isSameOrBefore(slotOne.interval.from) ||
    // if one slot is Busy type, then it should not count as overlapping with a non-Busy slot
    (slotOne.type === SlotType.BUSY) !== (slotTwo.type === SlotType.BUSY)

  return !notOverlapping
}

const canFitSlotIntoColumn = (
  column: TimetableSlot[],
  newSlot: TimetableSlot,
) => column.every((slot) => !getSlotsAreOverlapping(slot, newSlot))

const canFitSlotIntoColumnByType = (
  column: TimetableSlot[],
  newSlot: TimetableSlot,
) => column.every((slot) => !getSlotsAreOverlappingAndSameType(slot, newSlot))

export const getSlotsPositions = (
  slots: TimetableSlot[] | [],
  columns = [] as any,
): any => {
  if (!slots.length) {
    return columns
  }

  const [currentSlot, ...otherSlots] = slots

  const matchingColumn = columns.find((column: any) =>
    canFitSlotIntoColumn(column, currentSlot),
  )

  if (matchingColumn) {
    matchingColumn.push(currentSlot)
  } else {
    columns.push([currentSlot])
  }
  return getSlotsPositions(otherSlots, columns)
}

export const getSlotsPositionsByType = (
  slots: TimetableSlot[] | [],
  columns = [] as any,
): any => {
  if (!slots.length) {
    return columns
  }

  const [currentSlot, ...otherSlots] = slots

  const matchingColumn = columns.find((column: any) =>
    canFitSlotIntoColumnByType(column, currentSlot),
  )

  if (matchingColumn) {
    matchingColumn.push(currentSlot)
  } else {
    columns.push([currentSlot])
  }
  return getSlotsPositionsByType(otherSlots, columns)
}

/**
 * Utility function to determine if a legacy offset position qualifies as a full-width card.
 * In the old position system, a negative number means it's full-width.
 */
export const isSlotPositionHalfWidth = (
  offsetPosition: number | undefined,
): boolean => offsetPosition != null && offsetPosition >= 0

/**
 * Utility function to determine if a TimetableSlotPosition qualifies as a full-width card.
 * Rhapsody's various Event cards have unique styles when at "half width" (actually means it's not full-width).
 */
export const isSlotPositionHalfWidthV2 = (
  slotPosition: TimetableSlotPosition | undefined,
): boolean => {
  if (!slotPosition) return false
  return slotPosition.span === 1 && slotPosition.divisions > 1
}

/**
 * Calculates a slot's width as a percentage from a TimetableSlotPosition.
 * If divisions is 0 or falsy, it will assume that the denominator is 1.
 */
export const getSlotWidthPercentage = (
  slotPosition: TimetableSlotPosition | undefined,
): string => {
  if (!slotPosition) return '100%'
  return `${((slotPosition.span ?? 1) / (slotPosition.divisions || 1)) * 100}%`
}

/**
 * Calculates a slot's left offset in pixels using an offset number (old method).
 * Slots are full-width, or half of the column's default width if >1 subcolumn.
 * A non-negative offsetPosition typically means there's more than 1 subcolumn.
 * A nullish or negative offsetPosition returns 'auto' for legacy reasons, rather
 * than 0px, as that's the style originally used when passing undefined.
 */
export const getSlotOffsetPixels = (
  offsetPosition: number | undefined,
  defaultColumnWidth: number,
): string => {
  const fallbackColumnWidth = 208
  return offsetPosition == null || offsetPosition < 0
    ? `auto`
    : `${(offsetPosition * (defaultColumnWidth ?? fallbackColumnWidth)) / 2}px`
}

/**
 * Calculates a slot's left offset as a percentage from a TimetableSlotPosition.
 * If divisions is 0 or falsy, it will assume that the denominator is 1.
 */
export const getSlotOffsetPercentage = (
  slotPosition: TimetableSlotPosition | undefined,
): string => {
  if (!slotPosition) return '0%'
  return `${((slotPosition.offset ?? 0) / (slotPosition.divisions || 1)) * 100}%`
}
const isBusySlot = (slot: TimetableSlot) => slot.type === SlotType.BUSY

/**
 * Determines if a BUSY slot is a reserved slot using TimetableEvent data.
 * For CVC-34823, replace eventMap/reservedEventId with a RESERVED SlotType.
 */
export const isBusySlotReserved = (
  slot: TimetableSlot,
  eventMap: Record<string, TimetableEvent> | undefined,
  reservedEventId: string | undefined,
): boolean => {
  if (!slot || !eventMap || !reservedEventId) return false
  const slotId = getSlotId(slot)
  return !!(
    isBusySlot(slot) &&
    slotId &&
    reservedEventId &&
    eventMap[slotId]?.typeId === reservedEventId
  )
}

export const getSlotOffsetPosition = (
  slot: TimetableSlot,
  positions: any[][],
  isAppointmentReservedEnabled = false,
) => {
  const positionIndex = positions.findIndex((position) =>
    position.includes(slot),
  )
  const otherPositions = R.remove(positionIndex, 1, positions)

  const hasConflictingSlotsInOtherPositions = otherPositions.some((position) =>
    position.some(
      (otherSlot) =>
        getSlotsAreOverlapping(slot, otherSlot) &&
        (!isAppointmentReservedEnabled ||
          isBusySlot(otherSlot) === isBusySlot(slot)),
    ),
  )

  return hasConflictingSlotsInOtherPositions ? positionIndex : -1
}

/**
 * Given a slot and positions array, calculate the appropriate TimetableSlotPosition
 * @param slot - The slot to find the positioning information for
 * @param positions - The positions array, representing which slots are in which sub-columns
 * @returns A TimetableSlotPosition describing the starting sub-column of the slot and it's width
 */
export const getSlotOffsetPositionV2 = (
  slot: TimetableSlot,
  positions: TimetableSlot[][],
): TimetableSlotPosition => {
  const positionIndex = positions.findIndex((position) =>
    position.includes(slot),
  )

  if (positionIndex < 0) {
    return getFullwidthSlotPosition()
  }

  const otherPositions = R.remove(positionIndex, 1, positions)

  const hasConflictingSlotsInOtherPositions = otherPositions.some((position) =>
    position.some((otherSlot) => getSlotsAreOverlapping(slot, otherSlot)),
  )

  const offset = hasConflictingSlotsInOtherPositions ? positionIndex : 0

  return {
    offset,
    span: 1,
    divisions: positions.length,
  }
}
export const getTodayAppointmentsByType = (
  schedules: BoardingSchedule[],
  appointmentsMap: Record<string, TimetableEvent>,
  types: string[],
  getDate: (slot: TimetableSlot) => string,
) => {
  const items = R.pluck('items', schedules)
  const slots = R.flatten(R.pluck('slots', R.flatten(items)))
  const checkOutTodaySlots = slots.filter((slot) =>
    moment(getDate(slot)).isSame(new Date(), 'day'),
  )

  return checkOutTodaySlots
    .map((slot) => appointmentsMap[getSlotId(slot)!])
    .filter(
      (appointment) =>
        appointment.state && types.includes(appointment.state.id),
    )
}

export const getAppointmentColorState = (state: string | undefined) =>
  AppointmentStateColorMap[state ?? 'DEFAULT'] ||
  AppointmentStateColorMap.DEFAULT

type GetAppointmentWarningLabel = {
  from?: string | Nil
  stateName?: string
  to?: string | Nil
}

const WARNING_THRESHOLD = 10 // minutes
const WAITING_WARNING_THRESHOLD = 15 // minutes

/*
  Status in (Scheduled, Contacted, Confirmed, Rescheduled)
  > 10 minutes after appointment start datetime
*/
const isAppointmentLate = ({ from, stateName }: GetAppointmentWarningLabel) => {
  // TODO: leave only Scheduled and Rescheduled status for now
  const lateCandidates = ['Scheduled', 'Rescheduled'] // ['Scheduled', 'Contacted', 'Confirmed', 'Rescheduled']
  const diff = moment().diff(moment(from), 'minute')
  const isLate =
    stateName && lateCandidates.includes(stateName) && diff > WARNING_THRESHOLD
  return { isLate, diff }
}

/*
  Status in (Arrived)
  > 15 minutes after appointment start datetime
*/
const isAppointmentWaiting = ({
  from,
  stateName,
}: GetAppointmentWarningLabel) => {
  const lateCandidates = ['Arrived']
  const diff = moment().diff(moment(from), 'minute')
  const isWaiting =
    stateName &&
    lateCandidates.includes(stateName) &&
    diff > WAITING_WARNING_THRESHOLD
  return { isWaiting, diff }
}

/*
  Status in (Admitted, Hospitalized, In progress)
  > 10 minutes after appointment end datetime
*/
const isAppointmentBehind = ({ to, stateName }: GetAppointmentWarningLabel) => {
  // TODO: remove behind options for now
  const behindCandidates = [] as any[] // ['Admitted', 'Hospitalized', 'In progress']
  const diff = moment().diff(moment(to), 'minute')
  const isBehind =
    stateName &&
    behindCandidates.includes(stateName) &&
    diff > WARNING_THRESHOLD
  return { isBehind, diff }
}

const humanizeDiff = (diff: number) => {
  const m = diff % 60
  const h = Math.floor((diff - m) / 60)
  const d = Math.floor(h / 24)

  if (d >= 1) {
    return i18n.t('Time:FORMAT.DAYS', { days: d })
  }

  return h > 0
    ? i18n.t('Time:FORMAT.HOURS', { hours: h, minutes: m })
    : i18n.t('Time:FORMAT.MINUTES', { minutes: m })
}

export const getAppointmentWarningLabel = ({
  from,
  to,
  stateName,
}: GetAppointmentWarningLabel) => {
  const { isLate, diff: lateDiff } = isAppointmentLate({ from, to, stateName })
  const { isBehind, diff: behindDiff } = isAppointmentBehind({
    from,
    to,
    stateName,
  })
  const { isWaiting, diff: waitingDiff } = isAppointmentWaiting({
    from,
    to,
    stateName,
  })

  return isWaiting
    ? i18n.t('Time:LABEL.WAITING', { time: humanizeDiff(waitingDiff) })
    : isLate
      ? i18n.t('Time:LABEL.LATE', { time: humanizeDiff(lateDiff) })
      : isBehind
        ? i18n.t('Time:LABEL.BEHIND', { time: humanizeDiff(behindDiff) })
        : ''
}

export const getAppointmentTimeRange = (appointment: TimetableEvent) => ({
  startTime: moment(appointment?.scheduledStartDatetime),
  endTime: moment(appointment?.scheduledEndDatetime),
})

export const getHourAppointmentRange = (appointment: TimetableEvent) => {
  const [dateOne, dateTwo] = R.values(getAppointmentTimeRange(appointment))
  return formatHourRange(dateOne, dateTwo)
}

export const getUrlTimetableDate = () =>
  getUrlSearchParam('date', window.location.search) || undefined

export const checkEventAppointmentType = (
  appointment: TimetableEvent | Nil,
  AppointmentType: AppointmentEventType,
) => {
  const typeId = appointment?.typeId || appointment?.type?.id
  return typeId && R.includes(typeId, R.pluck('id', AppointmentType.subTypes))
}

/**
 * Basic TimetableSlotPosition for a full-width slot, if no positioning is available.
 */
export const getFullwidthSlotPosition = (): TimetableSlotPosition => ({
  offset: 0,
  span: 1,
  divisions: 1,
})

/**
 * Deterministic comparator for TimetableSlots - used to order slots along the timetable.
 * Elements that are sorted earlier appear first in the timetable/scheduler.
 * BUSY slots are sorted later to ensure they're placed on the rightmost side.
 * Slots are sorted by duration before start time, for better packing behavior.
 * @param a - First TimetableSlot being compared
 * @param b - Second TimetableSlot being compared
 * @param eventMap - Mapping of slot IDs to events, used to distinguish if a slot is Reserved.
 * @param reservedEventId - ID of reserved event type, used to distinguish if a slot is Reserved.
 * @return <1 if a comes before b, >1 if a comes after b, 0 if a & b are equal.
 */
export const sortSlotsForPositions = (
  a: TimetableSlot,
  b: TimetableSlot,
  eventMap?: Record<string, TimetableEvent>,
  reservedEventId?: string,
): number => {
  const slotTypePriority: Record<SlotType, number> = {
    [SlotType.APPOINTMENT]: 1,
    [SlotType.BUSY]: 3,
    [SlotType.NOT_AVAILABLE]: 4,
  }

  // If slot type is not found, sort slot last (rightmost)
  let aType = slotTypePriority[a.type] ?? 6
  let bType = slotTypePriority[b.type] ?? 6

  // If slots are reserved slots, adjust type sorting priority
  if (isBusySlotReserved(a, eventMap, reservedEventId)) aType = aType - 1
  if (isBusySlotReserved(b, eventMap, reservedEventId)) bType = bType - 1
  if (aType !== bType) {
    return aType - bType
  }

  // After slot type comparison, criteria is duration, startTime, name, then ID.
  const durationComparison = sortSlotsByDuration(a, b)
  if (durationComparison !== 0) return durationComparison
  const startComparison = sortSlotsByStartTime(a, b)
  if (startComparison !== 0) return startComparison
  const nameComparison = sortSlotsByName(a, b, eventMap)
  if (nameComparison !== 0) return nameComparison
  return sortSlotsById(a, b)
}

/**
 * Deterministic comparator for TimetableSlots - used to determine render order.
 * Elements that are sorted later will be rendered on top of other slots.
 * APPOINTMENT slots are sorted later to ensure they're placed over BUSY slots.
 * Slots are sorted by start time before duration, for better render ordering.
 * @param a - First TimetableSlot being compared
 * @param b - Second TimetableSlot being compared
 * @param eventMap - Mapping of slot IDs to events, used to distinguish if a slot is Reserved.
 * @param reservedEventId - ID of reserved event type, used to distinguish if a slot is Reserved.
 * @return <1 if a comes before b, >1 if a comes after b, 0 if a & b are equal.
 */
export const sortSlotsForRendering = (
  a: TimetableSlot,
  b: TimetableSlot,
  eventMap?: Record<string, TimetableEvent>,
  reservedEventId?: string,
): number => {
  const slotTypePriority: Record<SlotType, number> = {
    [SlotType.NOT_AVAILABLE]: 1,
    [SlotType.BUSY]: 3,
    [SlotType.APPOINTMENT]: 4,
  }

  // If slot type is not found, sort slot first (rendered at bottom)
  let aType = slotTypePriority[a.type] ?? 0
  let bType = slotTypePriority[b.type] ?? 0

  // If slots are reserved slots, adjust type sorting priority
  if (isBusySlotReserved(a, eventMap, reservedEventId)) aType = aType - 1
  if (isBusySlotReserved(b, eventMap, reservedEventId)) bType = bType - 1
  if (aType !== bType) {
    return aType - bType
  }

  // After slot type comparison, criteria is start time, duration, name, then ID.
  const startComparison = sortSlotsByStartTime(a, b)
  if (startComparison !== 0) return startComparison
  const durationComparison = sortSlotsByDuration(a, b)
  if (durationComparison !== 0) return durationComparison
  const nameComparison = sortSlotsByName(a, b, eventMap)
  if (nameComparison !== 0) return nameComparison
  return sortSlotsById(a, b)
}

/**
 * Sorts TimetableSlots by duration in descending order (longest slot first).
 * @param a - First TimetableSlot being compared
 * @param b - Second TimetableSlot being compared
 * @return <1 if A is longer, >1 if A is shorter, 0 if duration is equal.
 */
export const sortSlotsByDuration = (
  a: TimetableSlot,
  b: TimetableSlot,
): number => {
  const aStart = new Date(a.interval.from)
  const bStart = new Date(b.interval.from)
  const aEnd = new Date(a.interval.to)
  const bEnd = new Date(b.interval.to)
  let aDuration = aEnd.getTime() - aStart.getTime()
  let bDuration = bEnd.getTime() - bStart.getTime()
  if (isNaN(aDuration)) aDuration = 0
  if (isNaN(bDuration)) bDuration = 0
  return bDuration - aDuration
}

/**
 * Sorts TimetableSlots by start time in ascending order (earliest slot first).
 * @param a - First TimetableSlot being compared
 * @param b - Second TimetableSlot being compared
 * @return <1 if A is earlier, >1 if A is later, 0 if start time is the same.
 */
export const sortSlotsByStartTime = (
  a: TimetableSlot,
  b: TimetableSlot,
): number => {
  const aStart = new Date(a.interval.from).getTime()
  const bStart = new Date(b.interval.from).getTime()
  if (isNaN(aStart) && isNaN(bStart)) return 0
  if (isNaN(aStart)) return 1
  if (isNaN(bStart)) return -1
  return aStart - bStart
}

/**
 * Sorts TimetableSlots by the even name, alphabetically
 * @param a - First TimetableSlot being compared
 * @param b - Second TimetableSlot being compared
 * @param eventMap - Mapping of slot IDs to TimetableEvents, to get
 * @return <1 if event A's name is alphabetically before B's,
 *         >1 if A's name is alphabetically after B's,
 *         0 if slots have the same event name or no eventMap provided.
 */
export const sortSlotsByName = (
  a: TimetableSlot,
  b: TimetableSlot,
  eventMap?: Record<string, TimetableEvent>,
): number => {
  if (eventMap) {
    const aId = getSlotId(a)
    const bId = getSlotId(b)
    const aName = eventMap[aId ?? -1]?.name ?? ''
    const bName = eventMap[bId ?? -1]?.name ?? ''
    if (aName < bName) return -1
    if (aName > bName) return 1
  }
  return 0
}

/**
 * Sorts TimetableSlots by the hash ID, alphabetically. Not very practical,
 * but used as a last, deterministic sort criteria if all else fails.
 * @param a - First TimetableSlot being compared
 * @param b - Second TimetableSlot being compared
 * @return <1 if event A's ID is alphabetically before B's,
 *         >1 if A's ID is alphabetically after B's,
 *         0 if slots have the same slot ID.
 */
export const sortSlotsById = (a: TimetableSlot, b: TimetableSlot): number => {
  const aId = getSlotId(a)
  const bId = getSlotId(b)
  if ((aId ?? '') < (bId ?? '')) return -1
  else if ((aId ?? '') > (bId ?? '')) return 1
  return 0
}
